/**
 * API utilities for making authenticated requests
 * Handles adding authorization headers and error handling for API calls
 */
import supabase from './supabaseClient';

/**
 * Get the current session from Supabase
 * @returns {Object|null} The current session or null if not authenticated
 */
const getSession = async () => {
  const { data } = await supabase.auth.getSession();
  return data.session;
};

/**
 * Make an authenticated API request with the proper authorization headers
 * 
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @returns {Promise} - The fetch promise
 */
export const authenticatedFetch = async (url, options = {}) => {
  // Get the current session
  const session = await getSession();
  
  // Enhanced debug logging
  console.log(`[AUTH-DEBUG] Making request to ${url}`);
  console.log(`[AUTH-DEBUG] Method: ${options.method || 'GET'}`);
  console.log(`[AUTH-DEBUG] Session exists: ${!!session}`);
  console.log(`[AUTH-DEBUG] Token exists: ${!!session?.access_token}`);
  
  // Special logging for FormData uploads
  const isFormData = options.body instanceof FormData;
  if (isFormData) {
    console.log(`[AUTH-DEBUG] Sending FormData upload request`);
    console.log(`[AUTH-DEBUG] FormData entries:`, Array.from(options.body.entries()).map(([key]) => key));
  }
  
  // Create headers with authorization if we have a session
  const headers = {
    ...(options.headers || {}),
  };
  
  // Only set Content-Type to application/json if it's not explicitly set
  // and we're not dealing with FormData (which needs its own Content-Type)
  if (!headers['Content-Type'] && !isFormData) {
    headers['Content-Type'] = 'application/json';
    console.log(`[AUTH-DEBUG] Setting Content-Type: application/json`);
  } else if (isFormData) {
    console.log(`[AUTH-DEBUG] FormData detected - browser will set content-type with boundary`);
    // IMPORTANT: Explicitly remove Content-Type for FormData if it was set
    // Let browser set it automatically with the correct multipart boundary
    delete headers['Content-Type'];
  }
  
  // Add the authorization header if we have a session
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
    // Mask token for security in logs
    const maskedToken = session.access_token.substring(0, 5) + '...' + 
      session.access_token.substring(session.access_token.length - 5);
    console.log(`[AUTH-DEBUG] Authorization header set with token: ${maskedToken}`);
  } else {
    console.warn(`[AUTH-DEBUG] No access token available for request to ${url}`);
    console.warn('[AUTH-DEBUG] User may need to sign in again or token refresh failed');
  }
  
  // Add withCredentials for CORS requests if not specified
  const fetchOptions = {
    ...options,
    headers,
    credentials: options.credentials || 'include' // Include cookies by default
  };
  
  console.log(`[AUTH-DEBUG] Final request options:`, {
    method: fetchOptions.method,
    headers: Object.keys(fetchOptions.headers),
    credentials: fetchOptions.credentials,
    bodyType: isFormData ? 'FormData' : typeof fetchOptions.body
  });
  
  try {
    // Make the request with the authorization header
    const response = await fetch(url, fetchOptions);
    
    console.log(`[AUTH-DEBUG] Response status: ${response.status} for ${url}`);
    console.log(`[AUTH-DEBUG] Response headers:`, {
      'content-type': response.headers.get('content-type'),
      'x-powered-by': response.headers.get('x-powered-by')
    });
    
    // If we get a 401 Unauthorized, it means our token is invalid or expired
    if (response.status === 401) {
      console.warn(`[AUTH-DEBUG] Authentication required or token expired for ${url}`);
      console.warn('[AUTH-DEBUG] Consider refreshing the token or redirecting to login');
    }
    
    return response;
  } catch (error) {
    console.error(`[AUTH-DEBUG] Network error for ${url}:`, error.message);
    throw error;
  }
};

/**
 * Helper for authenticated GET requests
 * @param {string} url - The URL to fetch
 * @param {Object} options - Additional fetch options
 * @returns {Promise} - The fetch promise
 */
export const apiGet = (url, options = {}) => {
  return authenticatedFetch(url, {
    ...options,
    method: 'GET',
  });
};

/**
 * Helper for authenticated POST requests
 * @param {string} url - The URL to fetch
 * @param {Object} data - The data to send in the request body
 * @param {Object} options - Additional fetch options
 * @returns {Promise} - The fetch promise
 */
export const apiPost = (url, data, options = {}) => {
  return authenticatedFetch(url, {
    ...options,
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * Helper for authenticated PUT requests
 * @param {string} url - The URL to fetch
 * @param {Object} data - The data to send in the request body
 * @param {Object} options - Additional fetch options
 * @returns {Promise} - The fetch promise
 */
export const apiPut = (url, data, options = {}) => {
  return authenticatedFetch(url, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * Helper for authenticated DELETE requests
 * @param {string} url - The URL to fetch
 * @param {Object} options - Additional fetch options
 * @returns {Promise} - The fetch promise
 */
export const apiDelete = (url, options = {}) => {
  return authenticatedFetch(url, {
    ...options,
    method: 'DELETE',
  });
};
